import React from 'react';

import styles from '../GemsPackage/GemsPackage.module.scss';
import background from '../../assets/images/crates/shop_crates_background.png';
import crates1 from '../../assets/images/crates/crates1.png';
import crates2 from '../../assets/images/crates/crates2.png';
import crates3 from '../../assets/images/crates/crates3.png';
import cratesCommander1 from '../../assets/images/crates/crates_commander_1.png';
import cratesCommander2 from '../../assets/images/crates/crates_commander_2.png';
import cratesCommander3 from '../../assets/images/crates/crates_commander_3.png';
import iconInfo from '../../assets/images/icon-info.png';
import perkCommon from '../../assets/images/perk-common.png';
import perkUncommon from '../../assets/images/perk-uncommon.png';
import perkRare from '../../assets/images/perk-rare.png';
import perkHeroic from '../../assets/images/perk-heroic.png';
import IntelCommon from '../../assets/images/intel/intel_common.png';
import IntelUncommon from '../../assets/images/intel/intel_uncommon.png';
import IntelRare from '../../assets/images/intel/intel_rare.png';
import IntelHeroic from '../../assets/images/intel/intel_heroic.png';
import WarToken from '../../assets/images/war_token.png';

import { RESOURCES_IMAGES, COMMANDER_RESOURCES_IMAGES, RARITY_TO_CONFIG_KEY } from '../../shared/constants';
import { formatNumber } from '../../shared/helpers';
import { buildPurchaseBonus } from '../../shared/purchaseBonusConfig';

// Artwork stays on the frontend - the backend only sends the type + quantity.
// Keys mirror the backend names lowercased ("Common" -> common).
const RARITY_TO_PERK_IMAGE = {
    common: perkCommon,
    uncommon: perkUncommon,
    rare: perkRare,
    heroic: perkHeroic,
};

const RARITY_TO_INTEL_IMAGE = {
    common: IntelCommon,
    uncommon: IntelUncommon,
    rare: IntelRare,
    heroic: IntelHeroic,
};

/**
 * The backend sends content keys in PascalCase ("Paper", "BasicManual",
 * "Common"). Every frontend map - RESOURCES_IMAGES, COMMANDER_RESOURCES_IMAGES,
 * the rarity maps above and config.resourcesNames - is keyed in camelCase, so
 * lowercasing the first letter lines them up exactly. Older backends sent the
 * rarity as a numeric index, which RARITY_TO_CONFIG_KEY still resolves.
 */
const contentKey = (name) => {
    if (name === null || name === undefined) return '';
    const raw = String(name);
    if (/^\d+$/.test(raw)) return RARITY_TO_CONFIG_KEY[raw] || '';
    return raw.charAt(0).toLowerCase() + raw.slice(1);
};

const crateTypes = ['captains', 'majors', 'colonels'];
const commanderCrateTypes = ['combat', 'elite', 'legendary'];

const CratesPackage = ({
    ImageId,
    OldPrice,
    NewPrice,
    OfferId,
    Type,
    selectOfferById,
    currency,
    openCrateInfoPopUp,
    config,
    WarTokens,
    ContentRarityToQuantity,
    ExpeditionResourceToQuantity,
    CommanderExperienceItemToQuantity,
    Gifts,
    isCommander = false,
    crateId
}) => {
    const crateIndex = isCommander ? crateId : ImageId;
    const crateType = isCommander ? commanderCrateTypes[crateIndex] : crateTypes[crateIndex];

    /**
     * Promo builds (build-promo-x2/x3/x5, ...) set REACT_APP_PROMO_MULTIPLYER to scale
     * crate perk quantities. Since crate contents now come from the backend
     * (ContentRarityToQuantity), the promo is applied as a multiplier on those
     * quantities instead of the old hardcoded per-tier values.
     * Variants: "<n>" (all crates), "<n>_regular_crates_only" (non-commander crates),
     * "<n>_captains_crate_only" (captains crate only).
     */
    const getPromoMultiplier = () => {
        const promo = process.env.REACT_APP_PROMO_MULTIPLYER;
        if (!promo) return 1;

        const factor = parseInt(promo, 10);
        if (!factor || factor <= 1) return 1;

        // Scope the promo to specific crate groups when the build variant requests it
        if (promo.includes('regular_crates_only') && isCommander) return 1;
        if (promo.includes('captains_crate_only') && crateType !== 'captains') return 1;

        return factor;
    };

    /** Perks (regular crates) / Intel (commander crates), by rarity. */
    const buildPerksFromContent = () => {
        if (!ContentRarityToQuantity) {
            return [];
        }

        const perkImages = isCommander ? RARITY_TO_INTEL_IMAGE : RARITY_TO_PERK_IMAGE;
        const promoMultiplier = getPromoMultiplier();

        return Object.entries(ContentRarityToQuantity)
            .filter(([, quantity]) => quantity)
            .map(([rarity, quantity]) => {
                const key = contentKey(rarity);
                return {
                    type: config[key] || rarity,
                    img: perkImages[key],
                    quantity: formatNumber(quantity * promoMultiplier),
                };
            });
    };

    /**
     * Resources (regular crates) / Experience items (commander crates).
     * Both arrive from the backend as { <Name>: quantity }; we only supply the
     * artwork and the localised label.
     */
    const buildResourcesFromContent = () => {
        const content = isCommander
            ? CommanderExperienceItemToQuantity
            : ExpeditionResourceToQuantity;

        if (!content) {
            return [];
        }

        const images = isCommander ? COMMANDER_RESOURCES_IMAGES : RESOURCES_IMAGES;

        return Object.entries(content)
            .filter(([, quantity]) => quantity)
            .map(([name, quantity]) => {
                const key = contentKey(name);
                return {
                    name: config.resourcesNames[key] || name,
                    img: images[key],
                    quantity: formatNumber(quantity),
                };
            });
    };

    // Only the artwork and display name live on the frontend now; the contents
    // come from the backend payload above.
    const cratesInfo = {
        // Regular crates
        captains: { img: crates1, name: config.cratesNames.captains },
        majors: { img: crates2, name: config.cratesNames.majors },
        colonels: { img: crates3, name: config.cratesNames.colonels },
        // Commander crates
        combat: { img: cratesCommander1, name: config.cratesNames.combat },
        elite: { img: cratesCommander2, name: config.cratesNames.elite },
        legendary: { img: cratesCommander3, name: config.cratesNames.legendary },
    };

    const clickOnCard = (isInfo, id, type, img, name) => {
        if (isInfo) {
            const info = {
                name: cratesInfo[crateType].name,
                crateContains: config.crateContains,
                // Commander crates hold Experience + Intel, regular crates hold
                // Resources + Perks, so the two headings differ per crate kind.
                resourcesContent: isCommander ? config.experienceContent : config.resourcesContent,
                resources: buildResourcesFromContent(),
                perksContent: isCommander ? config.intelContent : config.perksContent,
                perks: buildPerksFromContent(),
                price: NewPrice,
                currency: currency,
                crateId: id,
                crateType: type,
                crateImg: img,
                crateName: name,
            };

            const purchaseBonus = buildPurchaseBonus(Gifts, config);

            if (purchaseBonus) {
                info.purchaseBonus = purchaseBonus;
            }

            openCrateInfoPopUp(info);
        } else {
            selectOfferById(id, type, img, name);
        }
    };

    return (
        <div className={styles.outerWrapper}>
            <div
                className={styles.infoClickArea}
                onClick={() =>
                    clickOnCard(
                        true,
                        OfferId,
                        Type,
                        cratesInfo[crateType].img,
                        cratesInfo[crateType].name
                    )
                }
            ></div>
            <div
                className={`${styles.wrapper} ${styles.crates}`}
                onClick={() =>
                    clickOnCard(
                        false,
                        OfferId,
                        Type,
                        cratesInfo[crateType].img,
                        cratesInfo[crateType].name
                    )
                }
            >
                <img src={background} alt={''} className={styles.background} />
                <div className={`${styles.name} ${styles.crates}`}>
                    {cratesInfo[crateType].name}
                </div>
                <img src={iconInfo} className={styles.info} alt={'img'} />
                <img
                    src={cratesInfo[crateType].img}
                    alt={''}
                    className={`${styles.gemsImage} ${styles.crates}${isCommander ? ` ${styles.commander}` : ''}`}
                />
                <div className={styles.pricesWrapper}>
                    <div className={styles.oldPrice}>
                        {OldPrice.toFixed(2)} {currency}
                        <div className={'stroke'}></div>
                    </div>
                    <div className={styles.newPrice}>
                        {NewPrice.toFixed(2)} {currency}
                    </div>
                </div>
                <div className="warTokensWrapper">
                    <div>+&nbsp;</div>
                    <div>{formatNumber(WarTokens)}</div>
                    <img className="warTokenImg" src={WarToken} alt='' />
                </div>
            </div>
        </div>
    );
};

export default CratesPackage;
